import { useState, useRef, useCallback, useEffect } from "react";
import { useSearchParams } from "react-router-dom";

import api from "services/api";

import getQueryParams from "common/get-query-params";
import encodeQueryParams from "common/encode-query-params";
import serveRequestErrors from "common/serve-request-errors";
import serveRequestSuccess from "common/serve-request-success";

import CONSTANTS from "constants/constant";

import ActionEnum from "enum/action";

import ErrorResponseDto from "dto/services/error-response-dto";
import PagePartnersDto, { PagePartnersTableCreatePayloadDto, PagePartnersTableQueryParamsDto, PagePartnersTableUpdatePayloadDto } from "dto/pages/partners/page-partners-dto";

import Button from "components/app-button";
import AppHeading from "components/app-heading";
import PartnersTable from "components/pages/partners/app-partners-table";

const PagePartners = () => {
	const [disableAddNew, setDisableAddNew] = useState(false);
	const [newRow, setNewRow] = useState<PagePartnersTableCreatePayloadDto>();
	const [searchParams, setSearchParams] = useSearchParams();
	const memoSetSearchParams = useRef(setSearchParams);
	const [isFetching, setIsFetching] = useState(false);
	const [partnerData, setPartnerData] = useState<PagePartnersDto>();
	const queryParams = useRef(getQueryParams<PagePartnersTableQueryParamsDto>(searchParams, { page: 0 }));

	const onHandleGetPartners = useCallback(async () => {
		setIsFetching(true);

		try {
			const { page } = queryParams.current;
			const payload = { size: 10, page };
			const response = await api.get.partners.partners(payload);

			setPartnerData(response.data.data.list);
		} catch (error) {
			const err = error as Error | ErrorResponseDto;

			serveRequestErrors(err);
		} finally {
			setIsFetching(false);
		}
	}, []);

	//prettier-ignore
	const onHandleAddPartner = useCallback(async (payload: PagePartnersTableCreatePayloadDto) => {
		let response = null;

		try {
			await api.post.partners.create(payload);

			response = true;
		} catch (error) {
			const err = error as Error | ErrorResponseDto;

			serveRequestErrors(err);
		}

		if (response) {
			serveRequestSuccess(CONSTANTS.MESSAGES.CREATED, ActionEnum.Create);

			if (partnerData && partnerData?.numberOfElements === partnerData.size) {
				queryParams.current.page = partnerData.totalElements % partnerData.size === 0 ? partnerData.totalPages : partnerData.totalPages - 1;
			}

			onHandleGetPartners();
		}
	}, [partnerData, onHandleGetPartners]);

	//prettier-ignore
	const onUpdatePartners = useCallback(async (payload: PagePartnersTableUpdatePayloadDto, page? : number) => {
		let response = null;
		
		try {
			await api.put.partners.update({ id: payload.id }, payload);
			
			response = true;
		} catch (error) {
			const err = error as Error | ErrorResponseDto;

			serveRequestErrors(err);
		}
		if (response) {
			serveRequestSuccess(CONSTANTS.MESSAGES.UPDATED, ActionEnum.Update);

			if (typeof page === "number") {
				queryParams.current.page = page;
			}

			onHandleGetPartners();
		}
	}, [onHandleGetPartners]);

	const onHandlePageChange = (page: number) => {
		queryParams.current.page = page;

		memoSetSearchParams.current(encodeQueryParams(queryParams.current));

		onHandleGetPartners();
	};

	const onHandleAddNew = () => {
		setDisableAddNew(true);
		setNewRow({ name: "", logo: "", status: "active" });
	};

	useEffect(() => {
		onHandleGetPartners();
	}, [onHandleGetPartners]);

	return (
		<div className="page-partners">
			<div className="partners">
				<AppHeading title="Partners">
					<AppHeading.Divider />
				</AppHeading>

				<div className="partners__button">
					<Button label="Add New" className="addNewBtn" disabled={disableAddNew} onClick={onHandleAddNew} />
				</div>

				{/* prettier-ignore */}
				<PartnersTable data={partnerData?.content || []} setDisableAddNew={setDisableAddNew} newRow={newRow} setNewRow={setNewRow} onUpdatePartners={onUpdatePartners} onCreatePartner={onHandleAddPartner} onHandlePageChange={onHandlePageChange} fetching={isFetching} paginationTable={{currentPage: partnerData?.number || 0, totalPages: partnerData?.totalPages || 0, totalElements: partnerData?.totalElements || 0, numberOfElements: partnerData?.numberOfElements || 0, tableSize: partnerData?.size || 10 }} />
			</div>
		</div>
	);
};

export default PagePartners;
